import React, { useState, useEffect, useMemo } from 'react'
import {
  Button as MaterialButton,
  IconButton,
  TextField,
} from '@material-ui/core'
import { connect } from 'react-redux'
import CloseIcon from '@material-ui/icons/Close'
import cn from 'classnames'
import { v4 as uuidv4 } from 'uuid'
import Button from 'components/common/Button/Button'
import { injectIntl } from 'react-intl'
import { ReactComponent as PlusIcon } from 'assets/img/plus-icon.svg'
import { ReactComponent as Bin } from 'assets/img/bin.svg'
import NestedSelectInput from 'components/common/Inputs/NestedSelectInput'
import { ReactComponent as Edit } from 'assets/img/edit-icon-redesign.svg'
import { Flex, RedAsterisk } from 'components/common/styles'
import { fetchTriggerInputs } from 'reducers/jobsTriggers/reducer'
import { BUTTON_TYPE_SECONDARY } from 'components/common/Button/constants'
import { cloneDeep, isEmpty } from 'lodash'
import { List } from 'immutable'
import {
  StyledActionSection,
  StyledButtonHolder,
  StyledCondition,
  StyledModal,
  StyledModalBoxSection,
  StyledModalOverlay,
  StyledName,
  StyledRuleSettings,
  StyledFormSection,
} from 'components/common/Modals/styles'
import { fetchTaskInputsTree } from 'reducers/jobTasksAdmin/reducer'
import {
  LOGIC_CONDITIONS_OPTIONS,
  LOGIC_OPERATORS_OPTIONS,
  BOOLEAN_INPUT_OPTIONS,
} from 'constants/conditionalLogic'
import SelectInput from 'components/common/Inputs/SelectInput'
import {
  ConditionalLogicContainer,
  ConditionalLogicHeader,
  ConditionRow,
  CriteriaContainer,
  Description,
  IconButtonContainer,
  IconsContainer,
  ModalTitle,
  Operator,
  SelectContainer,
  SelectedConditionalBlock,
} from './ConditionalLogic.styles'
import {
  ConditionalLogicProps,
  ConditionalValue,
  FilteredInput,
  CRITERIA,
  Option,
  OPERATORS,
  Input,
  TaskInputsItem,
  TreeItem,
} from './ConditionalLogic.types'
import messages from './ConditionalLogic.messages'
import { findInTree, getValidValue } from './ConditionalLogic.utils'

export const ConditionalLogicComponent = ({
  intl: { formatMessage },
  featureFlags,
  input,
  formData,
  fetchTriggerInputs,
  inputsList,
  isFetching,
  fetchTaskInputsTree,
  taskInputs,
  isInputsTreeFetching,
}: ConditionalLogicProps) => {
  const [isSidebarOpened, setIsSidebarOpened] = useState(false)
  const [isEdit, setIsEdit] = useState<number | null>(null)
  const [selectedConditionalLogic, setSelectedConditionalLogic] = useState<
    ConditionalValue
  >({} as ConditionalValue)

  useEffect(() => {
    if (isSidebarOpened) {
      fetchTriggerInputs({ id: formData.workflow.id })
      fetchTaskInputsTree({ taskId: formData.id })
    } else {
      setIsEdit(null)
    }
  }, [isSidebarOpened])

  const toggleIsSidebarOpened = () => setIsSidebarOpened(!isSidebarOpened)

  const formatListWithTranslation = (
    list: { label: string; value: string | number }[],
  ) =>
    list.map(({ value, label }) => ({
      value,
      label:
        label === 'is'
          ? formatMessage(messages[label]).toLowerCase()
          : formatMessage(messages[label]),
    }))

  const formattedLogicOperators = formatListWithTranslation(
    LOGIC_OPERATORS_OPTIONS,
  )

  const formattedLogicConditions = {
    number: formatListWithTranslation(
      LOGIC_CONDITIONS_OPTIONS.filter(({ inputTypes }) =>
        inputTypes.includes('number'),
      ),
    ),
    other: formatListWithTranslation(
      LOGIC_CONDITIONS_OPTIONS.filter(
        ({ inputTypes }) => !inputTypes.includes('number'),
      ),
    ),
  }

  const filteredInputs = useMemo(
    () =>
      inputsList
        ?.toJS()
        .find((task: TaskInputsItem) => task.task_id === formData.id)
        ?.inputs?.filter((item: Input) =>
          ['select', 'boolean', 'number'].includes(item.type as string),
        )
        ?.map((item: Input) => ({
          value: item.id,
          label: item.title,
          options: item.options,
          type: item.type,
          multiple: Boolean(item.multiple),
        })) || [],
    [JSON.stringify(inputsList), JSON.stringify(formData)],
  )

  const isLoading = useMemo(() => isInputsTreeFetching || isFetching, [
    isInputsTreeFetching,
    isFetching,
  ])

  const taskInputsTree = useMemo(() => taskInputs.toArray(), [
    JSON.stringify(taskInputs),
  ])

  const isSaveDisabled = useMemo(() => {
    const hasInvalidConditions = selectedConditionalLogic?.conditions?.some(
      cond => !cond.input || isEmpty(cond.values),
    )

    if (hasInvalidConditions) {
      return true
    }

    return (
      selectedConditionalLogic?.input_groups?.length === 0 &&
      selectedConditionalLogic.inputs?.length === 0
    )
  }, [JSON.stringify(selectedConditionalLogic)])

  const renderConditionCriteria = (criteria: CRITERIA): string => {
    switch (criteria) {
      case CRITERIA.IN:
      case CRITERIA.EQ:
        return ' ='
      case CRITERIA.LT:
        return ' <'
      case CRITERIA.LTE:
        return ' <='
      case CRITERIA.GT:
        return ' >'
      case CRITERIA.GTE:
        return ' >='
      case CRITERIA.NEQ:
      case CRITERIA.NOT_IN:
      default:
        return ' !='
    }
  }

  const sanitizeConditionalBlock = (
    block: ConditionalValue,
  ): ConditionalValue => {
    const {
      population_values,
      outcome_type,
      ...rest
    } = block as ConditionalValue & {
      population_values?: Record<string, string>
      outcome_type?: string
    }

    return rest
  }

  return (
    <ConditionalLogicContainer>
      <ConditionalLogicHeader>
        {formatMessage(messages.logicalBlock)}
      </ConditionalLogicHeader>
      <div>
        {input.value.map((item, index: number) => (
          <SelectedConditionalBlock
            key={item.uuid || uuidv4()}
            justify="space-between">
            <Flex direction="column" wrap="wrap">
              {item.conditions.map((cond, condIndex: number) => {
                const currentInput = filteredInputs.find(
                  ({ value }: FilteredInput) => value === cond.input,
                )

                return (
                  <ConditionRow key={uuidv4()}>
                    <Flex>
                      <Operator>
                        {condIndex
                          ? item.operator.toUpperCase()
                          : formatMessage(messages.ifCondition).toUpperCase()}
                      </Operator>
                      <Description>
                        &quot;
                        {cond.input_title || currentInput?.label}
                        &quot;
                      </Description>
                      <CriteriaContainer>
                        {renderConditionCriteria(cond.criteria)}
                      </CriteriaContainer>
                      <Description>
                        &quot;
                        {Array.isArray(cond.values)
                          ? cond.values
                              .map(
                                (
                                  val: string | number | Record<string, string>,
                                ) => {
                                  const selectedValue = currentInput?.options?.find(
                                    ({ value }: Option) => value === val,
                                  )?.label

                                  return typeof selectedValue === 'object' &&
                                    selectedValue &&
                                    selectedValue.title
                                    ? selectedValue.title
                                    : selectedValue || val
                                },
                              )
                              .toString()
                          : Object.values(cond.values)
                              .map(
                                (
                                  el: string | number | Record<string, string>,
                                ) =>
                                  typeof el === 'object' && el && el.title
                                    ? el.title
                                    : el,
                              )
                              .toString()}
                        &quot;
                      </Description>
                    </Flex>
                  </ConditionRow>
                )
              })}
              <ConditionRow>
                <Flex>
                  <Operator>
                    {formatMessage(messages.then).toUpperCase()}
                  </Operator>
                  <ConditionRow marginLeft={8}>
                    &quot;
                    {`${formatMessage(messages.show)}: `}
                    {[
                      ...(item.input_groups?.map(group =>
                        typeof group !== 'object'
                          ? taskInputsTree.find(({ id }) => id === +group)
                              ?.title
                          : group?.title ?? '',
                      ) || []),
                      ...(item.inputs
                        ?.filter(
                          (inp): inp is string | number | Input =>
                            inp !== null && inp !== undefined,
                        )
                        .map(inp =>
                          typeof inp !== 'object'
                            ? inputsList
                                ?.toJS()
                                .find(
                                  ({ task_id }: TaskInputsItem) =>
                                    task_id === formData.id,
                                )
                                ?.inputs?.find(({ id }: Input) => id === +inp)
                                ?.title ?? ''
                            : inp.title,
                        ) || []),
                    ]
                      .filter(Boolean)
                      .join(', ')}
                    &quot;
                  </ConditionRow>
                </Flex>
              </ConditionRow>
            </Flex>
            <IconsContainer>
              <IconButton
                aria-label="edit condition"
                onClick={() => {
                  setSelectedConditionalLogic(
                    sanitizeConditionalBlock(cloneDeep(input.value[index])),
                  )
                  toggleIsSidebarOpened()
                  setIsEdit(index)
                }}>
                <Edit />
              </IconButton>
              <IconButton
                aria-label="delete condition"
                onClick={() => {
                  const newInput = [...input.value]
                  newInput.splice(index, 1)
                  input.onChange(newInput)
                }}>
                <Bin />
              </IconButton>
            </IconsContainer>
          </SelectedConditionalBlock>
        ))}
      </div>
      <MaterialButton
        onClick={() => {
          toggleIsSidebarOpened()
          setSelectedConditionalLogic({
            operator: OPERATORS.AND,
            conditions: [
              {
                criteria: CRITERIA.IN,
                input: null,
                values: [],
              },
            ],
            input_groups: [],
            inputs: [],
          })
        }}
        className="add-button">
        + {formatMessage(messages.newLogicBlock)}
      </MaterialButton>
      <StyledModalOverlay
        className={cn({ closed: !isSidebarOpened })}
        onClick={toggleIsSidebarOpened}
      />
      <StyledModal
        customWidth="480px"
        opened={isSidebarOpened}
        direction="column"
        isNewHeader={featureFlags.APP_HEADER_V2}>
        <StyledFormSection>
          <StyledModalBoxSection>
            <ModalTitle align="center" justify="space-between">
              <div>{formatMessage(messages.logicalBlock)}</div>
              <IconButton size="small" onClick={toggleIsSidebarOpened}>
                <CloseIcon height="20px" />
              </IconButton>
            </ModalTitle>
          </StyledModalBoxSection>
          <StyledModalBoxSection>
            <StyledCondition align="center" wrap="wrap" marginBottom={10}>
              <StyledName>
                {formatMessage(messages.ifCondition).toUpperCase()}
              </StyledName>
              <SelectInput
                disabled={isLoading}
                required
                options={formattedLogicOperators}
                value={selectedConditionalLogic?.operator}
                onChange={(value: OPERATORS) => {
                  setSelectedConditionalLogic({
                    ...selectedConditionalLogic,
                    operator: value,
                  })
                }}
              />
              <Description>{formatMessage(messages.conditionText)}</Description>
            </StyledCondition>
            {selectedConditionalLogic?.conditions?.map(
              (item, index: number) => {
                const currentInput = filteredInputs.find(
                  ({ value }: FilteredInput) => value === item.input,
                )
                const isNumberInputSelected =
                  currentInput && currentInput.type === 'number'
                const selectCriteriaOptions = isNumberInputSelected
                  ? formattedLogicConditions.number
                  : formattedLogicConditions.other
                const conditionInputValueSelect = Array.isArray(item.values)
                  ? item.values
                  : Object.keys(item.values)
                const conditionInputValueNumber =
                  conditionInputValueSelect[0] || 0
                const isInputDisabled = !item.input || isLoading
                const chooseValueOptions =
                  currentInput?.options?.map(({ value, label }: Option) => ({
                    value,
                    label: typeof label === 'string' ? label : label.title,
                  })) || BOOLEAN_INPUT_OPTIONS

                return (
                  <StyledRuleSettings key={uuidv4()}>
                    <StyledCondition align="center" marginBottom={4}>
                      <StyledName width={45}>
                        {formatMessage(messages.input)}
                        <RedAsterisk>*</RedAsterisk>
                      </StyledName>
                      <SelectInput
                        disabled={isLoading}
                        label={formatMessage(messages.chooseInput)}
                        customWidth="305px"
                        required
                        options={filteredInputs}
                        value={item.input}
                        onChange={(value: string) => {
                          const newInput = { ...selectedConditionalLogic }
                          newInput.conditions[index].input = value
                          newInput.conditions[index].values = []
                          setSelectedConditionalLogic(newInput)
                        }}
                      />
                      {selectedConditionalLogic?.conditions?.length > 1 && (
                        <IconButtonContainer>
                          <IconButton
                            onClick={() => {
                              const newInput = { ...selectedConditionalLogic }
                              newInput.conditions.splice(index, 1)
                              setSelectedConditionalLogic(newInput)
                            }}>
                            <Bin />
                          </IconButton>
                        </IconButtonContainer>
                      )}
                    </StyledCondition>
                    <StyledCondition align="center" marginBottom={16}>
                      {currentInput && (
                        <>
                          <StyledName width={45}>
                            {formatMessage(messages.value)}
                            <RedAsterisk>*</RedAsterisk>
                          </StyledName>
                          <SelectInput
                            disabled={isInputDisabled}
                            customWidth="85px"
                            required
                            options={selectCriteriaOptions}
                            value={item.criteria}
                            onChange={(value: CRITERIA) => {
                              const newInput = { ...selectedConditionalLogic }
                              newInput.conditions[index].criteria = value
                              setSelectedConditionalLogic(newInput)
                            }}
                          />
                          {isNumberInputSelected ? (
                            <TextField
                              disabled={isInputDisabled}
                              required
                              value={conditionInputValueNumber}
                              onChange={({ target: { value } }) => {
                                const newInput = { ...selectedConditionalLogic }
                                newInput.conditions[index].values = [value]
                                setSelectedConditionalLogic(newInput)
                              }}
                            />
                          ) : (
                            <SelectInput
                              disabled={isInputDisabled}
                              label={formatMessage(messages.chooseValue)}
                              multiple
                              customWidth="212px"
                              required
                              options={chooseValueOptions}
                              value={conditionInputValueSelect}
                              onChange={(value: string[]) => {
                                const newInput = { ...selectedConditionalLogic }
                                newInput.conditions[index].values = value
                                setSelectedConditionalLogic(newInput)
                              }}
                            />
                          )}
                        </>
                      )}
                    </StyledCondition>
                    {index + 1 ===
                    selectedConditionalLogic?.conditions?.length ? (
                      <StyledCondition align="center">
                        <Button
                          startIcon={<PlusIcon />}
                          onClick={() => {
                            const newInput = { ...selectedConditionalLogic }
                            newInput.conditions.push({
                              criteria: CRITERIA.IN,
                              input: null,
                              values: [],
                            })
                            setSelectedConditionalLogic(newInput)
                          }}
                          type={BUTTON_TYPE_SECONDARY}>
                          {formatMessage(messages.addCondition)}
                        </Button>
                      </StyledCondition>
                    ) : (
                      <hr />
                    )}
                  </StyledRuleSettings>
                )
              },
            )}
          </StyledModalBoxSection>
          <StyledModalBoxSection noBorder>
            <StyledCondition align="center" wrap="wrap" marginBottom={10}>
              <StyledName>
                {formatMessage(messages.then).toUpperCase()}
              </StyledName>
            </StyledCondition>
            <StyledRuleSettings>
              <StyledCondition align="center" wrap="wrap">
                <StyledName width={45}>
                  {formatMessage(messages.show)}
                  <RedAsterisk>*</RedAsterisk>
                </StyledName>
                <SelectContainer>
                  <NestedSelectInput
                    hideEmptyGroups={false}
                    hasClearOption
                    groupSelectable
                    disabled={isLoading}
                    required
                    multiple
                    hasSearchField
                    label={formatMessage(messages.chooseInput)}
                    options={taskInputsTree}
                    value={[
                      ...(
                        selectedConditionalLogic?.input_groups?.map(inp =>
                          getValidValue(inp),
                        ) || []
                      ).reduce(
                        (acc: string[], el) => [
                          ...acc,
                          ...(findInTree(
                            taskInputsTree,
                            el,
                            'input_group',
                          )?.inputs?.map(({ id }) => String(id)) || []),
                        ],
                        [],
                      ),
                      ...(selectedConditionalLogic?.inputs
                        ?.filter(
                          (inp): inp is string | number | Input =>
                            inp !== null && inp !== undefined,
                        )
                        .map(inp => getValidValue(inp)) || []),
                    ]}
                    onChange={(value: (string | number)[]) => {
                      const newInput = { ...selectedConditionalLogic }
                      const selectedGroups = value.filter(
                        (val: number | string) =>
                          findInTree(taskInputsTree, val, 'input_group'),
                      )
                      const selectedInputs = value.filter(
                        (val: number | string) =>
                          findInTree(taskInputsTree, val, 'input'),
                      )

                      const filteredSelectedGroups = taskInputsTree.filter(
                        ({ input_item_type, inputs }: TreeItem) =>
                          input_item_type === 'input_group' &&
                          inputs?.some(
                            ({ id }) =>
                              value.includes(String(id)) || value.includes(id),
                          ),
                      )

                      filteredSelectedGroups.forEach(({ id, inputs }) => {
                        if (!value.includes(String(id))) {
                          const selectedInputsInInputGroup: string[] = []
                          inputs?.forEach(({ id: inputId }) => {
                            if (value.includes(String(inputId))) {
                              selectedInputsInInputGroup.push(String(inputId))
                            }
                          })
                          if (
                            selectedInputsInInputGroup.length !== inputs?.length
                          ) {
                            selectedInputs.push(...selectedInputsInInputGroup)
                          } else if (!selectedGroups.includes(String(id))) {
                            selectedGroups.push(String(id))
                          }
                        } else if (selectedGroups.includes(String(id))) {
                          const groupIndex = selectedGroups.indexOf(String(id))
                          const isInputsExists = inputs?.some(inputItem =>
                            value.includes(String(inputItem.id)),
                          )
                          if (isInputsExists && groupIndex > -1) {
                            selectedGroups.splice(groupIndex, 1)
                          }
                        }
                      })

                      newInput.inputs = selectedInputs
                      newInput.input_groups = selectedGroups
                      setSelectedConditionalLogic(newInput)
                    }}
                    isLoading={isLoading}
                  />
                </SelectContainer>
              </StyledCondition>
            </StyledRuleSettings>
          </StyledModalBoxSection>
        </StyledFormSection>
        <StyledActionSection align="center">
          <StyledButtonHolder>
            <Button
              type="primary"
              disabled={isLoading || isSaveDisabled}
              onClick={() => {
                const newInput = [...input.value]
                const blockToSave = sanitizeConditionalBlock(
                  selectedConditionalLogic,
                )

                if (isEdit === null) {
                  input.onChange([...newInput, blockToSave])
                } else {
                  newInput[isEdit] = blockToSave
                  input.onChange(newInput)
                }

                toggleIsSidebarOpened()
              }}>
              {formatMessage(messages.save)}
            </Button>
            <Button type="secondary" onClick={() => toggleIsSidebarOpened()}>
              {formatMessage(messages.cancel)}
            </Button>
          </StyledButtonHolder>
        </StyledActionSection>
      </StyledModal>
    </ConditionalLogicContainer>
  )
}

export default connect(
  (state: {
    getIn: (
      path: Array<string>,
    ) =>
      | Record<string, boolean>
      | boolean
      | List<TaskInputsItem>
      | List<TreeItem>
  }) => ({
    featureFlags: state.getIn(['profile', 'profile', 'featureFlags']) as Record<
      string,
      boolean
    >,
    inputsList: state.getIn(['jobsTriggers', 'triggerInputs']) as List<
      TaskInputsItem
    >,
    isFetching: state.getIn(['jobsTriggers', 'isFetching']) as boolean,
    taskInputs: state.getIn(['jobTasksAdmin', 'taskInputsTree']) as List<
      TreeItem
    >,
    isInputsTreeFetching: state.getIn([
      'jobTasksAdmin',
      'isInputsTreeFetching',
    ]) as boolean,
  }),
  {
    fetchTriggerInputs,
    fetchTaskInputsTree,
  },
)(injectIntl(ConditionalLogicComponent))
